// src/pages/ReservationPage.js
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BookingDateTimeCard from '../components/BookingDateTimeCard';
import 'react-datepicker/dist/react-datepicker.css';

import { AuthContext } from '../context/AuthContext';
import { useReservationForm } from '../hooks/useReservationForm';
import SegmentedControl from '../components/SegmentedControl';
import ReservationParticipantFields from '../components/ReservationParticipantFields';

import { 
  CheckCircle2, Clock, Users, ChevronRight, 
  CreditCard, ShieldCheck, Sparkles, X
} from 'lucide-react';

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";
import { RESERVATION_TYPES } from '../data/reservationData'; 

// Importação das Capas de RPG
import dnd from './about/assets/images/dnd.jpg';
import cthulhu from './about/assets/images/cthulhu.png';
import cthulhupulp from './about/assets/images/cthulhupulp.jpg';
import tormenta from './about/assets/images/tormenta20.jpg';
import vampire from './about/assets/images/vampire.png';
import ordem from './about/assets/images/ordem.jpg';

// --- BANCOS DE IMAGENS ---
const FOOD_IMAGES = {
    'Bolo de Chocolate': 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=500&q=80',
    'Torta de Chocolate': 'https://www.midwestliving.com/thmb/ZKPOV30d93FXPlzXOnMH_SHHg1s=/750x0/filters:no_upscale():max_bytes(150000):strip_icc():focal(1455x0:1457x2)/101925885_chocolate-espresso-pie-c9faeb2eaadf433f98dec7a1a068f075.jpg',
    'Sanduiche A': 'https://i.ytimg.com/vi/xdrb988Rb8w/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLClspCGk532D9RmY0R2hKG51dM1sQ',
    'Sanduiche B': 'https://www.southernliving.com/thmb/YB43P1UYm4F0Hf8kB-nEyalQJMw=/750x0/filters:no_upscale():max_bytes(150000):strip_icc()/27854_OE_CroissantSand_367-71abd1d7149f44b3b6344e7dba790c73.jpg',
    'Sanduiche C': 'https://www.seriouseats.com/thmb/WRE6_PiMZ-nfmov_IFbsJ6IqsGo=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/__opt__aboutcom__coeus__resources__content_migration__serious_eats__seriouseats.com__images__2017__05__20170501-sandwich-recipe-roundup-01-01203a6950b64f3d84683c6f8a8ffa2e.jpg',
    'default': 'https://images.unsplash.com/photo-1495147466023-af5c19cb3738?auto=format&fit=crop&w=500&q=80'
};

const RPG_IMAGES = {
    'Dungeons & Dragons': dnd,
    'Vampiro: A Máscara': vampire,
    'Call of Cthulhu': cthulhu,
    'Cthulhu Pulp': cthulhupulp,
    'Tormenta 20': tormenta,
    'Ordem Paranormal': ordem,
    'default': dnd
};

// --- SUBCOMPONENTES DE UI DA RESERVA ---

const StepHeader = ({ step, title, subtitle }) => (
    <div className="mb-8 mt-12">
        <div className="flex items-center gap-3 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#dbcdac]/30 text-[#971253] font-serif font-bold text-sm">
                {step}
            </span>
            <h2 className="font-serif text-3xl text-[#971253]">{title}</h2>
        </div>
        {subtitle && <p className="text-stone-500 font-light ml-11">{subtitle}</p>}
    </div>
);

const TypeCard = ({ id, title, icon: Icon, isSelected, onClick, priceBadge, benefits }) => (
    <div 
        onClick={() => onClick({ target: { value: id } })} 
        className={`
            relative p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 flex flex-col items-center text-center overflow-hidden
            ${isSelected 
                ? 'bg-[#971253]/5 border-[#971253] shadow-[0_10px_20px_-10px_rgba(151,18,83,0.3)] scale-[1.02]' 
                : 'bg-white border-[#dbcdac]/40 hover:border-[#528a89]/50 hover:shadow-md'
            }
        `}
    >
        {isSelected && (
            <div className="absolute top-3 right-3 text-[#971253]">
                <CheckCircle2 size={20} className="animate-in zoom-in duration-300" />
            </div>
        )}
        <div className={`p-4 rounded-full mb-4 transition-colors ${isSelected ? 'bg-[#971253] text-[#FFF5EE]' : 'bg-[#528a89]/10 text-[#528a89]'}`}>
            <Icon size={32} strokeWidth={1.5} />
        </div>
        <h3 className={`font-serif text-xl mb-1 ${isSelected ? 'text-[#971253]' : 'text-stone-700'}`}>{title}</h3>
        <span className="text-xs font-bold uppercase tracking-wider text-stone-400 bg-stone-100 px-3 py-1 rounded-full mt-2">
            {priceBadge}
        </span>

        {/* Benefits Drawer - APARECE APENAS EM MOBILE (md:hidden) */}
        <div className={`
            md:hidden grid transition-all duration-500 ease-in-out w-full
            ${isSelected ? 'grid-rows-[1fr] mt-6 opacity-100' : 'grid-rows-[0fr] mt-0 opacity-0'}
        `}>
            <div className="overflow-hidden">
                <div className="pt-5 border-t border-[#dbcdac]/40 flex flex-col gap-3 text-left">
                    {benefits && benefits.map((benefit, index) => (
                        <div key={index} className="flex items-start gap-3">
                            <CheckCircle2 size={16} className="text-[#528a89] shrink-0 mt-0.5" />
                            <span className="text-sm text-stone-600 leading-snug font-light">{benefit}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const SelectionCard = ({ title, imageSrc, isSelected, onClick }) => (
    <div 
        onClick={onClick}
        className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 border-2 flex flex-col group
        ${isSelected ? 'border-[#971253] ring-4 ring-[#971253]/10 shadow-md scale-[1.02]' : 'border-[#dbcdac]/40 hover:border-[#528a89]/50 hover:shadow-sm'}`}
    >
        {isSelected && (
            <div className="absolute top-2 right-2 bg-white rounded-full text-[#971253] shadow-sm z-10">
                <CheckCircle2 size={20} className="animate-in zoom-in" />
            </div>
        )}
        <div className="h-32 sm:h-40 w-full bg-stone-200 overflow-hidden">
            <img 
                src={imageSrc} 
                alt={title} 
                // 👇 Adicionamos a classe 'object-top' aqui
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" 
            />
        </div>
        <div className={`p-3 text-center text-[13px] sm:text-sm leading-tight font-bold flex-grow flex items-center justify-center ${isSelected ? 'bg-[#971253]/5 text-[#971253]' : 'bg-white text-stone-600'}`}>
            {title}
        </div>
    </div>
);

const BenefitsGrid = ({ benefits }) => {
    if (!benefits || benefits.length === 0) return null;
    return (
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 p-6 rounded-2xl animate-in fade-in duration-500 bg-[#971253]/5 border border-[#971253] shadow-[0_10px_20px_-10px_rgba(151,18,83,0.3)]">
            {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#971253] shrink-0 mt-0.5" />
                    <span className="text-sm text-stone-700 font-medium leading-relaxed">{benefit}</span>
                </div>
            ))}
        </div>
    );
};


// --- COMPONENTE PRINCIPAL ---

function ReservationPage() {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [selectionModal, setSelectionModal] = useState(null); 

    const initialData = useMemo(() => location.state?.prefillData || {}, [location.state]);

    const {
        reservationType, selectedDate, selectedTime, numPeopleState, participants,
        price, originalPrice, dynamicPrices, discountAmount, showNumPeopleInput, currentLimits, showRpgSystemInput, // 👇 IMPORTAMOS O ORIGINAL PRICE AQUI
        rpgSystems, selectedRpgSystem, handleRpgSystemChange, showDesertInput,
        desertOptions, selectedDesert, handleDesertChange, 
        sandwichOptions, selectedSandwich, handleSandwichChange, 
        handleTypeChange, handleDateChange, handleTimeChange, availableTimes, disabledDays,
        handleParticipantChange, verifySubscriber, hasDuplicateError, hasExpiredSubscribersForBooking, 
        setNumPeopleDirectly, createReservation, isSubmitting
    } = useReservationForm(initialData);

    const handleSplitPayment = (e) => { e.preventDefault(); createReservation('split'); };
    const handleTotalPayment = (e) => { e.preventDefault(); createReservation('total'); };

    const peopleOptions = useMemo(() => {
        const options = [];
        if (currentLimits) {
            for (let i = currentLimits.min; i <= currentLimits.max; i++) {
                options.push({ value: i, label: i.toString() });
            }
        }
        return options;
    }, [currentLimits]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (user === undefined) { 
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FFF5EE]">
                <div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#971253] rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800">
            <title>{`${Config.SITE_TITLE} - Reserva`}</title>
            <link rel="icon" href={ICON} />
            <link rel="canonical" href={`${Config.SITE_DOMAIN}/booking`} />
            <meta name="description" content="Board Game Café do Cariri" />

            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="max-w-4xl mx-auto px-6 relative z-10">
                
                <div className="text-center mb-16">
                    <Sparkles className="mx-auto w-10 h-10 text-[#dbcdac] mb-4" />
                    <h1 className="font-serif text-5xl md:text-6xl text-[#971253] mb-4">Sua Próxima Aventura</h1>
                    <p className="text-lg text-[#528a89] font-light">Configure sua experiência. Nós preparamos a mesa.</p>
                </div>

                <form className="space-y-12">
                    
                    {/* STEP 1: TIPO DE RESERVA */}
                    <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm">
                        <StepHeader step="1" title="Escolha a Experiência" subtitle="Qual jornada sua turma fará hoje?" />
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {RESERVATION_TYPES.map((type) => {
                                let currentBadgeText = type.priceBadge;
                                if (dynamicPrices) {
                                    if (type.id === 'boardgame') currentBadgeText = `R$${dynamicPrices.boardgame} por Pessoa`;
                                    else if (type.id === 'rpg') currentBadgeText = `Mesa Fechada R$${dynamicPrices.rpg}`;
                                    else if (type.id === 'kids') currentBadgeText = `R$${dynamicPrices.kids} por criança`;
                                }

                                return (
                                    <TypeCard 
                                        key={type.id}
                                        id={type.id} 
                                        title={type.title} 
                                        icon={type.icon} 
                                        priceBadge={currentBadgeText} 
                                        isSelected={reservationType === type.id} 
                                        onClick={handleTypeChange} 
                                        benefits={type.benefits}
                                    />
                                );
                            })}
                        </div>

                        <BenefitsGrid benefits={RESERVATION_TYPES.find(t => t.id === reservationType)?.benefits || []} />

                    </div>

                    {/* EXTRAS (Dinâmicos) */}
                    {(showRpgSystemInput || showDesertInput) && (
                        <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm animate-in slide-in-from-bottom-4 duration-500">
                            <StepHeader step="+" title="Detalhes da Experiência" subtitle={showRpgSystemInput ? "Escolha o cenário da sua campanha" : "Personalize o banquete da sua turma"} />
                            
                            <div className={`grid gap-10 ${showDesertInput ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                                
                                {showRpgSystemInput && (
                                    <div className="space-y-4">
                                        <label className="font-serif text-xl text-[#528a89]">Sistema de RPG</label>
                                        {/* Alterado: removemos o md:grid-cols-5 para manter 3 colunas no desktop */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {rpgSystems.map(system => (
                                                <SelectionCard 
                                                    key={system}
                                                    title={system}
                                                    imageSrc={RPG_IMAGES[system] || RPG_IMAGES.default}
                                                    isSelected={selectedRpgSystem === system}
                                                    onClick={() => setSelectionModal({ 
                                                        type: 'rpg', 
                                                        item: system, 
                                                        imageSrc: RPG_IMAGES[system] || RPG_IMAGES.default 
                                                    })}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {showDesertInput && (
                                    <>
                                        <div className="space-y-4">
                                            <label className="font-serif text-xl text-[#528a89]">Sobremesa Especial</label>
                                            <div className="grid grid-cols-2 gap-3">
                                                {desertOptions.map(desert => (
                                                    <SelectionCard 
                                                        key={desert}
                                                        title={desert}
                                                        imageSrc={FOOD_IMAGES[desert] || FOOD_IMAGES.default}
                                                        isSelected={selectedDesert === desert}
                                                        onClick={() => setSelectionModal({ 
                                                            type: 'desert', 
                                                            item: desert, 
                                                            imageSrc: FOOD_IMAGES[desert] || FOOD_IMAGES.default 
                                                        })}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <label className="font-serif text-xl text-[#528a89]">Seleção de Salgados</label>
                                            <div className="grid grid-cols-2 gap-3">
                                                {sandwichOptions.map(sandwich => (
                                                    <SelectionCard 
                                                        key={sandwich}
                                                        title={sandwich}
                                                        imageSrc={FOOD_IMAGES[sandwich] || FOOD_IMAGES.default}
                                                        isSelected={selectedSandwich === sandwich}
                                                        onClick={() => setSelectionModal({ 
                                                            type: 'sandwich', 
                                                            item: sandwich, 
                                                            imageSrc: FOOD_IMAGES[sandwich] || FOOD_IMAGES.default 
                                                        })}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>
                    )}

                    {/* STEP 2: DATA E HORA */}
                   
                        <BookingDateTimeCard 
                            selectedDate={selectedDate}
                            onDateSelect={handleDateChange}
                            disabledDays={disabledDays} 
                            reservationType={reservationType}
                            availableTimes={availableTimes}
                            selectedTime={selectedTime}
                            onTimeSelect={handleTimeChange}
                        />

                    {/* STEP 3: PESSOAS & PARTICIPANTES */}
                    {showNumPeopleInput && (
                        <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm">
                            <StepHeader step="3" title="Sua Turma" subtitle="Quem fará parte dessa aventura?" />
                            
                            <div className="mb-10">
                                <SegmentedControl
                                    label="Tamanho do Grupo"
                                    options={peopleOptions}
                                    selectedValue={numPeopleState}
                                    onChange={setNumPeopleDirectly} 
                                />
                            </div>

                            {reservationType !== 'kids' && participants.length > 0 && (
                                <div className="space-y-6 pt-8 border-t border-[#dbcdac]/40">
                                    <h3 className="font-serif text-2xl text-[#528a89] flex items-center gap-2"><Users size={24}/> Identificação</h3>
                                    <div className="space-y-4">
                                        {participants.map((participant, index) => (
                                            <div key={index} className="bg-[#FFF5EE]/50 p-6 rounded-2xl border border-[#dbcdac]/40 relative">
                                                <div className="absolute -left-3 -top-3 w-8 h-8 rounded-full bg-[#528a89] text-white flex items-center justify-center font-bold text-sm shadow-md">
                                                    {index + 1}
                                                </div>
                                                <ReservationParticipantFields
                                                    index={index}
                                                    participant={participant}
                                                    onChange={handleParticipantChange}
                                                    onPhoneBlur={verifySubscriber}
                                                    isPrimaryUser={index === 0}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 4: CHECKOUT / RESUMO */}
                    <div className="bg-gradient-to-br from-[#528a89] to-[#3a6362] p-8 md:p-12 rounded-[2rem] shadow-2xl text-white relative overflow-hidden">
                        <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                        
                        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-10">
                            
                            {/* 👇 LÓGICA CONDICIONAL DO DE/POR APLICADA AQUI 👇 */}
                            <div className="w-full md:w-1/2 space-y-2 text-center md:text-left">
                                <h3 className="font-serif text-3xl text-[#dbcdac] mb-4">Resumo da Reserva</h3>
                                
                                {discountAmount > 0 ? (
                                    <div className="flex flex-col items-center md:items-start">
                                        <div className="flex items-center gap-2 text-white/70 mb-1">
                                            <span className="text-lg">De:</span>
                                            <span className="text-2xl line-through decoration-[#971253] decoration-2">{originalPrice}</span>
                                        </div>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-xl text-[#dbcdac]">Por:</span>
                                            <span className="font-serif text-5xl md:text-6xl font-bold text-white tracking-tight">{price}</span>
                                        </div>
                                        <div className="mt-4 text-[#dbcdac] text-sm font-medium flex items-center justify-center md:justify-start gap-2 bg-black/20 w-fit px-4 py-1.5 rounded-full mx-auto md:mx-0 border border-white/10">
                                            <Sparkles size={16} className="text-[#dbcdac]" />
                                            Desconto de Assinante Aplicado
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-baseline justify-center md:justify-start gap-2">
                                        <span className="text-xl opacity-80">Total:</span>
                                        <span className="font-serif text-5xl md:text-6xl font-bold text-white tracking-tight">{price}</span>
                                    </div>
                                )}

                                {hasExpiredSubscribersForBooking && selectedDate && (
                                    <div className="mt-4 bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 flex items-start gap-3 text-left max-w-sm mx-auto md:mx-0 animate-in fade-in slide-in-from-bottom-2">
                                        <span className="text-yellow-300 mt-0.5">⚠️</span>
                                        <p className="text-yellow-100 text-sm leading-snug">
                                            Assinatura expira antes da data da reserva.<br/>
                                            <strong className="text-white">Renove para garantir seu desconto.</strong>
                                        </p>
                                    </div>
                                )}

                                <p className="text-sm text-white/70 flex items-center justify-center md:justify-start gap-1 mt-4">
                                    <ShieldCheck size={32}/> Pagamento seguro e confirmação instantânea.
                                </p>
                            </div>

                            <div className="w-full md:w-1/2 flex flex-col gap-4">
                                <button 
                                    type="button" 
                                    onClick={handleTotalPayment}
                                    disabled={isSubmitting || hasDuplicateError}
                                    className="w-full py-5 bg-[#971253] text-[#FFF5EE] rounded-xl font-bold tracking-wider text-lg hover:bg-[#7a0e42] hover:-translate-y-1 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                                >
                                    <CreditCard size={20} />
                                    {hasDuplicateError ? 'Corrija o Número Duplicado' : (isSubmitting ? 'Preparando a Mesa...' : 'Pagar Valor Total')}
                                </button>

                                {(reservationType === 'boardgame' || reservationType === 'deliparty')  && (
                                    <button 
                                        type="button" 
                                        onClick={handleSplitPayment}
                                        disabled={isSubmitting || hasDuplicateError}
                                        className="w-full py-4 bg-transparent border-2 border-white/30 text-white rounded-xl font-medium tracking-wide hover:bg-white/10 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {hasDuplicateError ? 'Corrija o Número Duplicado' : (isSubmitting ? 'Aguarde...' : 'Pagar Separado')}
                                    </button>
                                )}
                            </div>
                            
                        </div>
                    </div>

                </form>
            </div>

            {/* --- MODAL DE SELEÇÃO (Comida ou RPG) --- */}
            {selectionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setSelectionModal(null)}
                    />
                    
                    <div className="relative bg-[#FFF5EE] w-full max-w-sm rounded-[2rem] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
                        <button 
                            type="button"
                            onClick={() => setSelectionModal(null)}
                            className="absolute top-4 right-4 z-10 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition-colors backdrop-blur-md"
                        >
                            <X size={20} />
                        </button>
                        
                        <div className="h-64 w-full">
                            <img 
                                src={selectionModal.imageSrc} 
                                alt={selectionModal.item} 
                                className="w-full h-full object-cover"
                            />
                        </div>
                        
                        <div className="p-8 text-center">
                            <h3 className="font-serif text-2xl font-bold text-[#971253] mb-2">{selectionModal.item}</h3>
                            <p className="text-stone-500 text-sm mb-8">
                                {selectionModal.type === 'desert' && "Uma sobremesa deliciosa para celebrar a sua Party na Deli da Persy."}
                                {selectionModal.type === 'sandwich' && "Salgadinhos feitos na hora para manter a energia do seu grupo lá no alto."}
                                {selectionModal.type === 'rpg' && "Prepare seus dados! Garantimos o mestre e todos os terrenos para esta aventura."}
                            </p>
                            
                            <button 
                                type="button"
                                onClick={() => {
                                    if (selectionModal.type === 'desert') {
                                        handleDesertChange({ target: { value: selectionModal.item } });
                                    } else if (selectionModal.type === 'sandwich') {
                                        handleSandwichChange({ target: { value: selectionModal.item } });
                                    } else if (selectionModal.type === 'rpg') {
                                        handleRpgSystemChange({ target: { value: selectionModal.item } });
                                    }
                                    setSelectionModal(null);
                                }}
                                className="w-full py-4 bg-[#528a89] hover:bg-[#3a6362] text-[#FFF5EE] rounded-xl font-bold tracking-wider text-lg transition-all shadow-lg hover:-translate-y-1"
                            >
                                Escolher Esse
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default ReservationPage;